import produce from 'immer';

import { Wallet, User, CompPoints } from '../../api/auth';

export enum REASON_FOR_UPDATING_USER {
  VISIT = 'VISIT',
  LOGIN = 'LOGIN',
  REGISTRATION = 'REGISTRATION',
  DEPOSIT = 'DEPOSIT',
  REFRESH_STATUSES = 'REFRESH_STATUSES',
}

export type UserState = {
  user: User | undefined;
  compPoints: CompPoints | undefined;
  reasonForUpdatingUser: REASON_FOR_UPDATING_USER | undefined;
  isAuthenticated: boolean;
  pendingUser: boolean;

  wallets: Wallet[];
  extendedWallets: Wallet[];
  pendingWallets: boolean;

  afterLoginRedirectUrl: string | undefined;
};

export enum USER_ACTION_TYPE {
  USER_FETCH,
  USER_INSERT,
  USER_RESET,
  USER_START_PENDING,
  USER_STOP_PENDING,
  USER_UPDATE_REASON,
  USER_REFRESH,

  WALLETS_FETCH,
  WALLETS_UPDATE,
  WALLETS_START_PENDING,
  WALLETS_STOP_PENDING,

  AFTER_LOGIN_REDIRECT_SET,
  AFTER_LOGIN_REDIRECT_RESET,
}

export type UserAction =
  | {
      type: USER_ACTION_TYPE.USER_FETCH;
      payload: {
        user: User | undefined;
        compPoints: CompPoints | undefined;
        reasonForUpdatingUser: REASON_FOR_UPDATING_USER | undefined;
        isAuthenticated: boolean;
        wallets: Wallet[];
        extendedWallets: Wallet[];
      };
    }
  | { type: USER_ACTION_TYPE.USER_REFRESH; payload: User }
  | {
      type: USER_ACTION_TYPE.USER_INSERT;
      payload: {
        user: User | undefined;
        compPoints: CompPoints | undefined;
        reasonForUpdatingUser: REASON_FOR_UPDATING_USER;
        isAuthenticated: boolean;
        wallets: Wallet[];
      };
    }
  | { type: USER_ACTION_TYPE.USER_RESET }
  | {
      type: USER_ACTION_TYPE.USER_UPDATE_REASON;
      payload: { reasonForUpdatingUser: REASON_FOR_UPDATING_USER };
    }
  | { type: USER_ACTION_TYPE.USER_START_PENDING }
  | { type: USER_ACTION_TYPE.USER_STOP_PENDING }
  | { type: USER_ACTION_TYPE.WALLETS_START_PENDING }
  | { type: USER_ACTION_TYPE.WALLETS_STOP_PENDING }
  | {
      type: USER_ACTION_TYPE.AFTER_LOGIN_REDIRECT_SET;
      payload: { url: string };
    }
  | { type: USER_ACTION_TYPE.AFTER_LOGIN_REDIRECT_RESET };

type UserReducer = (state: UserState, action: UserAction) => UserState;

export const userReducer: UserReducer = (state, action) => {
  return produce(state, (draft) => {
    switch (action.type) {
      case USER_ACTION_TYPE.USER_FETCH:
        draft.user = action.payload.user;
        draft.reasonForUpdatingUser = action.payload.reasonForUpdatingUser;
        draft.isAuthenticated = action.payload.isAuthenticated;
        draft.wallets = action.payload.wallets;
        draft.extendedWallets = action.payload.extendedWallets;
        draft.compPoints = action.payload.compPoints;
        break;

      case USER_ACTION_TYPE.USER_REFRESH:
        draft.user = action.payload;
        draft.reasonForUpdatingUser = REASON_FOR_UPDATING_USER.REFRESH_STATUSES;
        break;

      case USER_ACTION_TYPE.USER_INSERT:
        draft.user = action.payload.user;
        draft.reasonForUpdatingUser = action.payload.reasonForUpdatingUser;
        draft.isAuthenticated = action.payload.isAuthenticated;
        draft.wallets = action.payload.wallets;
        draft.compPoints = action.payload.compPoints;
        break;

      case USER_ACTION_TYPE.USER_RESET:
        draft.user = undefined;
        draft.reasonForUpdatingUser = REASON_FOR_UPDATING_USER.VISIT;
        draft.isAuthenticated = false;
        draft.wallets = [];
        break;

      case USER_ACTION_TYPE.USER_UPDATE_REASON:
        draft.reasonForUpdatingUser = action.payload.reasonForUpdatingUser;
        break;

      case USER_ACTION_TYPE.USER_START_PENDING:
        draft.pendingUser = true;
        break;

      case USER_ACTION_TYPE.USER_STOP_PENDING:
        draft.pendingUser = false;
        break;

      case USER_ACTION_TYPE.WALLETS_START_PENDING:
        draft.pendingWallets = true;
        break;

      case USER_ACTION_TYPE.WALLETS_STOP_PENDING:
        draft.pendingWallets = false;
        break;

      case USER_ACTION_TYPE.AFTER_LOGIN_REDIRECT_SET:
        draft.afterLoginRedirectUrl = action.payload.url;
        break;

      case USER_ACTION_TYPE.AFTER_LOGIN_REDIRECT_RESET:
        draft.afterLoginRedirectUrl = undefined;
        break;

      default:
        break;
    }
  });
};
